import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { authVerify, formatApiError } from "../lib/api";
import { useUser } from "../context/UserContext";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const { refresh, user } = useUser();
  const [status, setStatus] = useState("verifying"); // verifying | ok | error
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setStatus("error");
      setError("Missing verification token.");
      return;
    }
    (async () => {
      try {
        await authVerify(token);
        if (cancelled) return;
        await refresh();
        setStatus("ok");
        setTimeout(() => {
          if (cancelled) return;
          nav(user?.role === "creator" ? "/settings" : "/dashboard", { replace: true });
        }, 1400);
      } catch (err) {
        if (cancelled) return;
        setError(formatApiError(err, "Verification failed."));
        setStatus("error");
      }
    })();
    return () => { cancelled = true; };
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div data-testid="verify-page" className="min-h-[80vh] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md text-center">
        {status === "verifying" && (
          <>
            <Loader2 className="text-[#00ff88] mx-auto mb-4 animate-spin" size={36} />
            <h1 data-testid="verify-status-verifying" className="text-2xl font-semibold text-white mb-2">Verifying your email…</h1>
            <p className="text-sm text-[#a0a0b0]">Hold on a moment.</p>
          </>
        )}
        {status === "ok" && (
          <>
            <CheckCircle2 className="text-[#00ff88] mx-auto mb-4" size={48} />
            <h1 data-testid="verify-status-ok" className="text-2xl font-semibold text-white mb-2">Email verified!</h1>
            <p className="text-sm text-[#a0a0b0]">Taking you to your dashboard…</p>
          </>
        )}
        {status === "error" && (
          <>
            <XCircle className="text-[#ff4444] mx-auto mb-4" size={48} />
            <h1 data-testid="verify-status-error" className="text-2xl font-semibold text-white mb-2">Verification failed</h1>
            <p className="text-sm text-[#ff8888] mb-5">{error}</p>
            <Link to="/login" data-testid="verify-login-link" className="cc-btn-primary inline-flex px-5 py-2 rounded-lg text-sm">
              Back to sign in
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
